/**
 * Data civil: `string` no formato `YYYY-MM-DD`, sem hora e sem fuso. É o tipo
 * de "que dia isso aconteceu?" — `transactionDate`, `dueDate`, `observedAt`.
 * Ver [[ADR-0008 - Dinheiro em centavos inteiros]] e
 * `Docs/03-Arquitetura/Dinheiro, datas e fuso.md`.
 *
 * **Nada aqui usa `Date`.** A aritmética é inteira, pelo algoritmo de dias-desde-
 * a-época de Howard Hinnant, e é isso que torna verdadeira a afirmação da nota
 * de que só existe **um** lugar convertendo instante em data civil: `timezone.ts`.
 * Com `Date`, `civil.ts` seria um segundo lugar onde um fuso poderia entrar —
 * `new Date(2026, 1, 1)` é meia-noite **local**, e `Date.UTC(50, 0, 1)` é 1950.
 */

/** `'2026-07-15'`. Comparável e ordenável como string, no código e no Firestore. */
export type CivilDate = string

const SHAPE = /^\d{4}-\d{2}-\d{2}$/

const MIN_YEAR = 0
const MAX_YEAR = 9999

/** Gregoriano proléptico: múltiplo de 4, exceto século não múltiplo de 400. */
function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

function assertYear(year: number): void {
  if (!Number.isInteger(year) || year < MIN_YEAR || year > MAX_YEAR) {
    throw new Error(`data civil: ano precisa ser inteiro de ${MIN_YEAR} a ${MAX_YEAR}, recebi ${String(year)}`)
  }
}

function assertMonth(month: number): void {
  if (!Number.isInteger(month) || month < 1 || month > 12) {
    throw new Error(`data civil: mês precisa ser inteiro de 1 a 12, recebi ${String(month)}`)
  }
}

function pad(value: number, size: number): string {
  return String(value).padStart(size, '0')
}

/**
 * Quantos dias tem o mês. A regra dos 400 anos inteira: 2000 é bissexto, 1900
 * não é. `switch` em vez de tabela indexada porque uma tabela sob
 * `noUncheckedIndexedAccess` exigiria um `?? 31` — um valor de fachada que
 * transformaria mês inválido em resposta plausível em vez de erro.
 */
export function lastDayOfMonth(year: number, month: number): number {
  assertYear(year)
  assertMonth(month)
  switch (month) {
    case 1:
    case 3:
    case 5:
    case 7:
    case 8:
    case 10:
    case 12:
      return 31
    case 4:
    case 6:
    case 9:
    case 11:
      return 30
    default:
      return isLeapYear(year) ? 29 : 28
  }
}

/**
 * Monta a data civil **saturando** o dia no último do mês: 31 de fevereiro vira
 * o dia 28 (ou 29). É o comportamento que o ciclo de fatura precisa — cartão
 * com fechamento no dia 31 fecha no último dia de fevereiro, não em 3 de março,
 * que é para onde `new Date(2026, 1, 31)` rola em silêncio.
 *
 * Saturar só para cima: dia menor que 1 é erro de quem chamou, não um valor a
 * consertar.
 *
 * Quem valida `year` e `month` é o `lastDayOfMonth` logo abaixo — repetir
 * `assertYear`/`assertMonth` aqui em cima era indistinguível por teste, do
 * mesmo jeito que era em `addDays`. A única entrada que separava as duas
 * versões tinha mês E dia inválidos ao mesmo tempo, e qual dos dois erros
 * ganha nesse caso não é contrato que valha congelar num teste.
 */
export function civilFromParts(year: number, month: number, day: number): CivilDate {
  if (!Number.isInteger(day) || day < 1) {
    throw new Error(`data civil: dia precisa ser inteiro a partir de 1, recebi ${String(day)}`)
  }
  const saturated = Math.min(day, lastDayOfMonth(year, month))
  return `${pad(year, 4)}-${pad(month, 2)}-${pad(saturated, 2)}`
}

/**
 * Valida forma **e** calendário: `'2026-02-30'` tem a forma certa e não existe.
 * É a guarda de fronteira — o que vem do Firestore, de um formulário ou de um
 * provedor passa por aqui antes de ser tratado como `CivilDate`.
 */
export function isCivilDate(v: unknown): v is CivilDate {
  if (typeof v !== 'string' || !SHAPE.test(v)) return false
  const year = Number(v.slice(0, 4))
  const month = Number(v.slice(5, 7))
  const day = Number(v.slice(8, 10))
  if (month < 1 || month > 12) return false
  return day >= 1 && day <= lastDayOfMonth(year, month)
}

function assertCivilDate(v: unknown): asserts v is CivilDate {
  if (!isCivilDate(v)) {
    throw new Error(`data civil: esperava 'YYYY-MM-DD' existente, recebi ${JSON.stringify(v)}`)
  }
}

/** Quebra a data nos três números. Recusa data que não existe. */
export function partsOf(date: CivilDate): { year: number; month: number; day: number } {
  assertCivilDate(date)
  return {
    year: Number(date.slice(0, 4)),
    month: Number(date.slice(5, 7)),
    day: Number(date.slice(8, 10)),
  }
}

/**
 * Comparador para `sort`. `YYYY-MM-DD` já ordena como string — é uma das razões
 * do formato — e esta função existe para dizer isso no lugar de o chamador
 * torcer para que seja verdade, e para recusar o que não é data.
 */
export function compareCivil(a: CivilDate, b: CivilDate): number {
  assertCivilDate(a)
  assertCivilDate(b)
  if (a < b) return -1
  if (a > b) return 1
  return 0
}

/**
 * Dias desde 1970-01-01, e a volta. Algoritmo de Howard Hinnant ("chrono-
 * Compatible Low-Level Date Algorithms"), gregoriano proléptico, aritmética
 * inteira e **sem `Date`** — que é o que mantém `timezone.ts` como o único
 * lugar do repositório onde um fuso pode entrar.
 *
 * A época interna do algoritmo é 0000-03-01 (março primeiro, para que o dia
 * bissexto caia no fim do ano); `719468` é a distância dela até 1970-01-01.
 */
function daysFromCivil(year: number, month: number, day: number): number {
  const shifted = month <= 2 ? year - 1 : year
  const era = Math.floor(shifted / 400)
  const yearOfEra = shifted - era * 400
  const dayOfYear = Math.floor((153 * (month + (month > 2 ? -3 : 9)) + 2) / 5) + day - 1
  const dayOfEra =
    yearOfEra * 365 + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100) + dayOfYear
  return era * 146097 + dayOfEra - 719468
}

function civilFromDays(days: number): { year: number; month: number; day: number } {
  const shifted = days + 719468
  const era = Math.floor(shifted / 146097)
  const dayOfEra = shifted - era * 146097
  const yearOfEra = Math.floor(
    (dayOfEra -
      Math.floor(dayOfEra / 1460) +
      Math.floor(dayOfEra / 36524) -
      Math.floor(dayOfEra / 146096)) /
      365,
  )
  const year = yearOfEra + era * 400
  const dayOfYear =
    dayOfEra - (365 * yearOfEra + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100))
  const monthPrime = Math.floor((5 * dayOfYear + 2) / 153)
  const day = dayOfYear - Math.floor((153 * monthPrime + 2) / 5) + 1
  const month = monthPrime + (monthPrime < 10 ? 3 : -9)
  return { year: month <= 2 ? year + 1 : year, month, day }
}

/**
 * Anda `days` dias no calendário. Aceita negativo.
 *
 * Quem valida `date` é o `partsOf` logo abaixo — um `assertCivilDate` aqui em
 * cima seria indistinguível por teste, e guarda que nenhum teste distingue é
 * exatamente o que a Fase 1 aprendeu a não deixar no código.
 *
 * O resultado passa por `civilFromParts`, então nasce válido — e a saturação
 * dele nunca dispara aqui, porque `civilFromDays` só produz dias que existem.
 */
export function addDays(date: CivilDate, days: number): CivilDate {
  if (!Number.isSafeInteger(days)) {
    throw new Error(`addDays: dias precisa ser inteiro, recebi ${String(days)}`)
  }
  const { year, month, day } = partsOf(date)
  const moved = civilFromDays(daysFromCivil(year, month, day) + days)
  return civilFromParts(moved.year, moved.month, moved.day)
}

/**
 * Dias civis de `de` até `ate`. **Negativo quando `ate` é anterior.**
 *
 * Existia como `diasEntre` dentro de `check-invoices-due.ts`, privada, com um
 * laço de `addDays` que devolvia **zero** quando a distância passava de 60 dias
 * ou quando a ordem vinha invertida — um "hoje" silencioso sobre uma fatura de
 * três meses atrás. A Fase 8 precisou da mesma conta para conciliar nota com
 * transação, e duas cópias de uma conta de datas é duas chances de o zero
 * silencioso aparecer de novo.
 *
 * Aqui é subtração de dia juliano: exata, sem laço, sem teto, e sem tocar em
 * `Date` — que é o que a decisão 79 proíbe justamente porque arrasta fuso para
 * dentro de uma conta que é sobre dias do calendário.
 */
export function daysBetween(de: CivilDate, ate: CivilDate): number {
  const a = partsOf(de)
  const b = partsOf(ate)
  return daysFromCivil(b.year, b.month, b.day) - daysFromCivil(a.year, a.month, a.day)
}

/**
 * `'2026-08-12'` → `'12/08'`.
 *
 * Mudou-se para cá ao ganhar o segundo dono — a linha do histórico e o herói do
 * detalhe. Duas cópias da mesma conta seriam duas chances de uma ganhar um
 * separador diferente, e a tela mostraria `12/08` num lugar e `12-08` no outro.
 *
 * Corta a string em vez de construir um `Date`: `CivilDate` **não tem fuso**, e
 * `new Date('2026-08-12')` a interpreta como meia-noite UTC — que no Brasil é o
 * dia 11. É o mesmo erro que a decisão 193 já pagou uma vez.
 */
export function dayAndMonth(date: CivilDate): string {
  return `${date.slice(8, 10)}/${date.slice(5, 7)}`
}
